/**
 * Workshop FileFrame — Gmail-style attachment card.
 *
 * Native-source note: no official OSS component exists for the Gmail
 * attachment chip, so the row is recreated pinned to spec (extension
 * tile + truncated name + human size, whole row clickable).
 *
 * Row recipe copied from taskboard list-view-row (title
 * text-appkit-caption, muted detail text-canvas-xs) with the closest
 * production fill bg-elevated/60 for the single glass row. Denker glass
 * outside. No FrameHeader/FrameFooter — hover pill only.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  Spinner,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface FileFrameProps {
  name?: string;
  kind?: string;
  size?: string;
  url?: string;
  status?: FrameStatus;
  onOpen?: (url: string) => void;
  className?: string;
  "data-testid"?: string;
}

const MOCK_FILE = {
  name: "Design-Systems-Keynote.pdf",
  kind: "pdf",
  size: "240 KB",
  url: "https://example.com/files/design-systems-keynote.pdf",
};

export function FileFrame({
  name = MOCK_FILE.name,
  kind = MOCK_FILE.kind,
  size = MOCK_FILE.size,
  url = MOCK_FILE.url,
  status = "done",
  onOpen,
  className,
  "data-testid": testId = "file-frame",
}: FileFrameProps) {
  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex w-[320px] flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div
        className="nodrag flex min-h-0 flex-1 flex-col justify-center gap-2.5 px-3 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        {status === "error" ? (
          <div
            className="flex items-center gap-2 rounded-preview bg-danger-muted px-3 py-4"
            data-testid={`${testId}-error`}
            role="alert"
          >
            <Icons.CircleAlert
              className="h-3.5 w-3.5 shrink-0 text-status-error"
              aria-hidden="true"
            />
            <span className="text-canvas-sm text-secondary">
              Couldn&apos;t load this file. Try again.
            </span>
          </div>
        ) : status === "streaming" ? (
          <div
            className="flex items-center gap-3 rounded-preview bg-elevated/60 px-3 py-2.5"
            data-testid={`${testId}-loading`}
            aria-label="Loading file"
          >
            <div
              className="h-11 w-11 shrink-0 animate-pulse rounded-preview bg-glass-fill-dense"
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5" aria-hidden="true">
              <div className="h-3 w-3/4 animate-pulse rounded bg-glass-fill-dense" />
              <div className="h-2.5 w-1/2 animate-pulse rounded bg-glass-fill-dense" />
            </div>
            <Spinner className="h-3 w-3 shrink-0" />
          </div>
        ) : !name ? (
          <div
            className="flex flex-col items-center gap-1.5 px-3 py-6 text-center"
            data-testid={`${testId}-empty`}
          >
            <Icons.FileText
              className="h-4 w-4 text-muted"
              aria-hidden="true"
            />
            <p className="text-canvas-sm italic text-muted">
              No file attached
            </p>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (url && onOpen) onOpen(url);
            }}
            data-testid={`${testId}-row`}
            aria-label={`Open ${name}`}
            className="flex w-full items-center gap-3 rounded-preview bg-elevated/60 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.1]"
          >
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-preview bg-glass-fill-dense"
              data-testid={`${testId}-kind`}
              aria-label={`${kind} file`}
            >
              <span className="text-canvas-2xs font-bold uppercase tracking-wide text-accent">
                {kind}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <span
                className="block truncate text-appkit-caption font-semibold text-primary"
                data-testid={`${testId}-name`}
              >
                {name}
              </span>
              {size && (
                <span
                  className="mt-0.5 block truncate text-canvas-xs text-muted"
                  data-testid={`${testId}-size`}
                >
                  {size}
                </span>
              )}
            </span>
            <Icons.ExternalLink
              className="h-3.5 w-3.5 shrink-0 text-muted"
              aria-hidden="true"
            />
          </button>
        )}

      </div>
    </div>
  );
}

export type { FileFrameProps };
